import React, { useState, useEffect, useCallback } from "react";
import "./dashboard.css";
import "./portal-components.css";
import { useAuth } from "../../context/AuthContext";
import { API_BASE_URL } from "../../config/apiConfig";

const Dashboard = () => {
  const { user } = useAuth();
  
  // State for storage data
  const [storageData, setStorageData] = useState({
    used: 0,
    total: 5,
    loading: true
  });
  
  // State for token balance
  const [tokenData, setTokenData] = useState({
    balance: 0,
    lifetimeEarned: 0,
    lifetimeSpent: 0,
    loading: true
  });

  // State for live nodes (registered agents)
  const [liveNodes, setLiveNodes] = useState({
    agents: [],
    loading: true
  });

  // State for backup jobs
  const [backupJobs, setBackupJobs] = useState({
    jobs: [],
    loading: true
  });

  // Fetch registered agents for Live Nodes
  const fetchLiveNodes = useCallback(async () => {
    if (!user?.username) return;
    try {
      const res = await fetch(
        `${API_BASE_URL}/agents/registered?username=${encodeURIComponent(user.username)}`
      );
      const data = await res.json();
      if (data.status === "success") {
        setLiveNodes({
          agents: data.data?.agents || [],
          loading: false
        });
      } else {
        setLiveNodes({ agents: [], loading: false });
      }
    } catch (err) {
      console.error("Failed to fetch live nodes:", err);
      setLiveNodes({ agents: [], loading: false });
    }
  }, [user?.username]);

  // Fetch backup jobs
  const fetchBackupJobs = useCallback(async () => {
    if (!user?.username) return;
    try {
      const res = await fetch(
        `${API_BASE_URL}/jobs?username=${encodeURIComponent(user.username)}`
      );
      const data = await res.json();
      if (data.status === "success") {
        // Get only upcoming/scheduled jobs
        const jobs = data.data?.jobs || [];
        const scheduledJobs = jobs.filter(j => j.is_enabled).slice(0, 5);
        setBackupJobs({
          jobs: scheduledJobs,
          loading: false
        });
      } else {
        setBackupJobs({ jobs: [], loading: false });
      }
    } catch (err) {
      console.error("Failed to fetch backup jobs:", err);
      setBackupJobs({ jobs: [], loading: false });
    }
  }, [user?.username]);

  // Fetch token balance
  const fetchTokenBalance = useCallback(async () => {
    if (!user?.username) return;
    try {
      const res = await fetch(
        `${API_BASE_URL}/tokens/balance?username=${encodeURIComponent(user.username)}`
      );
      const data = await res.json();
      if (data.status === "success") {
        setTokenData({
          balance: data.data?.balance || 0,
          lifetimeEarned: data.data?.lifetime_earned || 0,
          lifetimeSpent: data.data?.lifetime_spent || 0,
          loading: false
        });
      }
    } catch (err) {
      console.error("Failed to fetch token balance:", err);
      setTokenData(prev => ({ ...prev, loading: false }));
    }
  }, [user?.username]);

  // Fetch storage usage
  const fetchStorageUsage = useCallback(async () => {
    if (!user?.username) return;
    try {
      const res = await fetch(
        `${API_BASE_URL}/archives/storage?username=${encodeURIComponent(user.username)}`
      );
      const data = await res.json();
      if (data.status === "success") {
        setStorageData({
          used: data.data?.used_gb || 0,
          total: data.data?.total_gb || 5,
          loading: false
        });
      } else {
        setStorageData({ used: 0, total: 5, loading: false });
      }
    } catch (err) {
      console.error("Failed to fetch storage:", err);
      setStorageData({ used: 0, total: 5, loading: false });
    }
  }, [user?.username]);

  useEffect(() => {
    fetchTokenBalance();
    fetchStorageUsage();
    fetchLiveNodes();
    fetchBackupJobs();

    // Refresh live nodes every 30 seconds
    const interval = setInterval(fetchLiveNodes, 30000);
    return () => clearInterval(interval);
  }, [fetchTokenBalance, fetchStorageUsage, fetchLiveNodes, fetchBackupJobs]);

  // Calculate storage percentage
  const storagePercent = storageData.total > 0 
    ? Math.min((storageData.used / storageData.total) * 100, 100) 
    : 0;

  // Get progress bar color based on usage
  const getProgressColor = (percent) => {
    if (percent >= 90) return "#dc2626";
    if (percent >= 70) return "#f59e0b";
    return "#16a34a";
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case "online": return "#16a34a";
      case "warning": return "#f59e0b";
      case "offline": 
      default: return "#dc2626";
    }
  };

  // Count online agents
  const onlineCount = liveNodes.agents.filter(a => a.connection_status === "online").length;
  const totalCount = liveNodes.agents.length;

  return (
    <>
      {/* Top 5% container */}
      <div className="right-top">
        <div className="right-top-title">Dashboard Overview</div>
      </div>

      {/* Middle fill container */}
      <div className="right-middle">
        {/* TOP ROW */}
        <div className="right-middle-top">
          <div className="right-middle-top-left cell">
            <div className="cell-header">
              <span>Live Nodes</span>
              <span className="header-badge">{onlineCount}/{totalCount} Online</span>
            </div>
            <div className="cell-body">
              {liveNodes.loading ? (
                <div className="cell-loading">Loading...</div>
              ) : liveNodes.agents.length === 0 ? (
                <div className="cell-empty">
                  <span>No agents registered</span>
                  <small>Go to Agent Management to register agents</small>
                </div>
              ) : (
                <div className="node-list">
                  {liveNodes.agents.map((agent) => (
                    <div key={agent.id} className="node-item">
                      <div className="node-status">
                        <span 
                          className="status-dot"
                          style={{ backgroundColor: getStatusColor(agent.connection_status) }}
                        />
                      </div>
                      <div className="node-info">
                        <div className="node-name">{agent.agent_name}</div>
                        <div className="node-details">
                          {agent.hostname} • {agent.ip_address || "No IP"}
                        </div>
                      </div>
                      <div className="node-meta">
                        <span className={`node-status-badge ${agent.connection_status}`}>
                          {agent.connection_status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="right-middle-top-right cell">
            <div className="cell-header">
              <span>Upcoming Backup Jobs</span>
              <span className="header-badge">{backupJobs.jobs.length} Scheduled</span>
            </div>
            <div className="cell-body">
              {backupJobs.loading ? (
                <div className="cell-loading">Loading...</div>
              ) : backupJobs.jobs.length === 0 ? (
                <div className="cell-empty">
                  <span>No backup jobs scheduled</span>
                  <small>Create jobs in Backup Jobs section</small>
                </div>
              ) : (
                <div className="job-list">
                  {backupJobs.jobs.map((job) => (
                    <div key={job.id} className="job-item">
                      <div className="job-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                          <polyline points="7 10 12 15 17 10"/>
                          <line x1="12" y1="15" x2="12" y2="3"/>
                        </svg>
                      </div>
                      <div className="job-info">
                        <div className="job-name">{job.job_name}</div>
                        <div className="job-schedule">{job.schedule_type || "Manual"}</div>
                      </div>
                      <div className="job-meta">
                        <span className="job-type-badge">{job.backup_type || "Full"}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* BOTTOM ROW */}
        <div className="right-middle-bottom">
          <div className="right-middle-bottom-left cell">
            <div className="cell-header">Agent Logs</div>
            <div className="cell-body">
              <div className="cell-empty">
                <span>No recent logs</span>
                <small>Agent activity will appear here</small>
              </div>
            </div>
          </div>

          <div className="right-middle-bottom-right cell">
            <div className="cell-header">Wizards</div>
            <div className="cell-body">
              <div className="wizard-list">
                <div className="wizard-item">
                  <div className="wizard-icon">🚀</div>
                  <div className="wizard-info">
                    <div className="wizard-name">Quick Backup</div>
                    <div className="wizard-desc">Start a backup now</div>
                  </div>
                </div>
                <div className="wizard-item">
                  <div className="wizard-icon">📦</div>
                  <div className="wizard-info">
                    <div className="wizard-name">Restore Files</div>
                    <div className="wizard-desc">Restore from backup</div>
                  </div>
                </div>
                <div className="wizard-item">
                  <div className="wizard-icon">⚙️</div>
                  <div className="wizard-info">
                    <div className="wizard-name">Configure Agent</div>
                    <div className="wizard-desc">Setup your agent</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* THIRD ROW - Token Balance & Used Storage */}
        <div className="right-middle-third">
          {/* Token Balance Card */}
          <div className="right-middle-third-left cell">
            <div className="cell-header">Token Balance</div>
            <div className="cell-body">
              {tokenData.loading ? (
                <div className="cell-loading">Loading...</div>
              ) : (
                <div className="token-content">
                  <div className="token-main">
                    <div className="token-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"/>
                        <path d="M12 6v12M8 10h8M8 14h8"/>
                      </svg>
                    </div>
                    <div className="token-info">
                      <span className="token-number">{tokenData.balance.toLocaleString()}</span>
                      <span className="token-label">Tokens Available</span>
                    </div>
                  </div>
                  <div className="token-value-row">
                    <span className="token-value">≈ ${(tokenData.balance * 0.01).toFixed(2)} USD</span>
                  </div>
                  <div className="token-stats-row">
                    <div className="token-stat">
                      <span className="stat-label">Earned</span>
                      <span className="stat-value positive">+{tokenData.lifetimeEarned.toLocaleString()}</span>
                    </div>
                    <div className="token-stat">
                      <span className="stat-label">Spent</span>
                      <span className="stat-value negative">-{tokenData.lifetimeSpent.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Used Storage Card */}
          <div className="right-middle-third-right cell">
            <div className="cell-header">Used Storage</div>
            <div className="cell-body">
              {storageData.loading ? (
                <div className="cell-loading">Loading...</div>
              ) : (
                <div className="storage-content">
                  <div className="storage-progress-section">
                    <div className="storage-progress-bar">
                      <div 
                        className="storage-progress-fill"
                        style={{ 
                          width: `${storagePercent}%`,
                          backgroundColor: getProgressColor(storagePercent)
                        }}
                      />
                    </div>
                    <div className="storage-progress-label">
                      <span className="storage-used">{storageData.used.toFixed(2)} GB</span>
                      <span className="storage-separator"> / </span>
                      <span className="storage-total">{storageData.total} GB</span>
                    </div>
                  </div>
                  <div className="storage-stats-row">
                    <div className="storage-stat">
                      <span className="stat-label">Used</span>
                      <span className="stat-value">{storagePercent.toFixed(1)}%</span>
                    </div>
                    <div className="storage-stat">
                      <span className="stat-label">Available</span>
                      <span className="stat-value">{(storageData.total - storageData.used).toFixed(2)} GB</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom 5% container */}
      <div className="right-bottom-bar"></div>
    </>
  );
};

export default Dashboard;
